<script lang="ts" setup>
import { computed, h, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuery } from "@tanstack/vue-query";
import { debounce } from "lodash";
import { NDataTable, NDropdown, NSpin, useMessage, type DataTableColumns } from "naive-ui";
import { ProperText, useAjiacoParams } from "@propertechnologies/ajiaco";
import { searchVendorsV2 } from "../../services/contador/vendors.service";
import type { VendorsSearchRequest, VendorsWhere, WhereAndOr } from "../../models/search";
import type { Comment, Vendor } from "../../models/contador";
import { usePortalBreakpoints } from "../../hooks/usePortalBreakpoints";
import NotesDrawer from "../../components/NotesDrawer.vue";
import { renderNotesCell } from "../../utils/notesColumn";

const router = useRouter();
const route = useRoute();
const { isMobile } = usePortalBreakpoints();
const message = useMessage();

function stripAccents(input: string): string {
    if (!input) return "";
    try {
        return input.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    } catch {
        return input;
    }
}

const { search, vendorId, drawerTab } = useAjiacoParams(
    {
        search: String,
        vendorId: String,
        drawerTab: String,
    },
    { router, route },
);

const debouncedSearch = ref<string>("");
const applySearch = debounce((val: string) => {
    debouncedSearch.value = stripAccents((val || "").trim());
}, 300);

watch(
    search,
    (val) => {
        applySearch(val || "");
    },
    { immediate: true },
);

const enabled = computed(() => true);

watch(
    () => debouncedSearch.value,
    () => {
        vendors.value = [];
        nextOffset.value = 0;
        lastPageCount.value = 0;
    },
);

const showDrawer = ref(false);
const openedVendor = ref<Vendor | null>(null);

const pageLimit = 50;
const vendors = ref<Vendor[]>([]);
const total = ref<number | undefined>();
const nextOffset = ref(0);
const lastPageCount = ref(0);
const isFetchingMore = ref(false);

const buildV2SearchPayload = (opts: {
    offset: number;
    limit: number;
    includeComments?: boolean;
}) => {
    const where: WhereAndOr<VendorsWhere> = { AND: [] };
    if (opts.includeComments) {
        where.AND!.push({ include_comments: { equals: true } });
    }

    const search = debouncedSearch.value;
    if (search) {
        where.OR = [{ name: { contains: search } }, { gl_account_id: { contains: search } }];
    }

    const payload: VendorsSearchRequest = {
        where,
        limit: opts.limit,
        offset: opts.offset,
    };
    return payload;
};

const { data: vendorsResponse, isLoading: loadingVendors } = useQuery({
    queryKey: computed(() => ["vendors-v2", pageLimit, debouncedSearch.value]),
    enabled,
    queryFn: async () => {
        return await searchVendorsV2(
            buildV2SearchPayload({ offset: 0, limit: pageLimit, includeComments: true }),
        );
    },
});

async function openDrawerForVendorId(id: string) {
    let entity = vendors.value.find((v) => v.id === id) || null;

    if (!entity) {
        try {
            const payload: VendorsSearchRequest = {
                where: {
                    AND: [{ id: { equals: id } }, { include_comments: { equals: true } }],
                } as WhereAndOr<VendorsWhere>,
                limit: 1,
                offset: 0,
            };
            const resp = await searchVendorsV2(payload);
            const items = resp?.items ?? [];
            if (items.length > 0) {
                entity = items[0] ?? null;
                if (entity && !vendors.value.some((v) => v.id === entity!.id)) {
                    vendors.value = [entity, ...vendors.value];
                }
            }
        } catch (e) {
            // silent fail
        }
    }

    if (!entity) {
        message.error("Vendor not found in current list");
        vendorId.value = undefined;
        return;
    }

    openedVendor.value = entity;
    showDrawer.value = true;
}

watch(
    vendorId,
    (id) => {
        if (!id) return;
        void openDrawerForVendorId(id);
    },
    { immediate: true },
);

watch(
    () => showDrawer.value,
    (isOpen) => {
        if (!isOpen) {
            vendorId.value = undefined;
        }
    },
);

watch(
    vendorsResponse,
    (response) => {
        if (!response) {
            vendors.value = [];
            total.value = 0;
            nextOffset.value = 0;
            lastPageCount.value = 0;
            return;
        }

        const items = response.items ?? [];
        vendors.value = items;
        total.value = response.total ?? items.length;
        nextOffset.value = ((response.offset ?? 0) as number) + items.length;
        lastPageCount.value = items.length;
    },
    { immediate: true },
);

const canLoadMore = computed(() => {
    if (total.value !== undefined && total.value !== null) {
        return vendors.value.length < total.value;
    }

    return lastPageCount.value === pageLimit;
});

const loadMoreVendors = async () => {
    if (isFetchingMore.value || loadingVendors.value || !canLoadMore.value) {
        return;
    }

    isFetchingMore.value = true;
    try {
        const response = await searchVendorsV2(
            buildV2SearchPayload({
                offset: nextOffset.value,
                limit: pageLimit,
                includeComments: true,
            }),
        );

        if (!response) {
            lastPageCount.value = 0;
            return;
        }

        const newItems = response.items ?? [];
        if (newItems.length > 0) {
            vendors.value = [...vendors.value, ...newItems];
            nextOffset.value += newItems.length;
        }
        lastPageCount.value = newItems.length;
        if (response.total !== undefined) {
            total.value = response.total;
        }
    } finally {
        isFetchingMore.value = false;
    }
};

const handleScroll = (event: Event) => {
    const target = event.target as HTMLElement;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining < 200) {
        void loadMoreVendors();
    }
};

function replaceItemsAtOffset(offset: number, newItems: Vendor[]) {
    if (offset < 0) offset = 0;
    if (vendors.value.length < offset) {
        vendors.value = [...vendors.value];
    }
    const before = vendors.value.slice(0, offset);
    const after = vendors.value.slice(offset + newItems.length);
    vendors.value = [...before, ...newItems, ...after];
}

// Refetch current and previous page and merge results
async function refetchPages() {
    const currentOffset = Math.max(nextOffset.value - lastPageCount.value, 0);
    const prevOffset = Math.max(currentOffset - pageLimit, 0);

    const [currentResp, prevResp] = await Promise.all([
        searchVendorsV2(
            buildV2SearchPayload({
                offset: currentOffset,
                limit: pageLimit,
                includeComments: true,
            }),
        ),
        currentOffset > 0
            ? searchVendorsV2(
                  buildV2SearchPayload({
                      offset: prevOffset,
                      limit: pageLimit,
                      includeComments: true,
                  }),
              )
            : Promise.resolve(undefined),
    ]);

    if (currentResp?.items) {
        replaceItemsAtOffset(currentOffset, currentResp.items);
        lastPageCount.value = currentResp.items.length;
        total.value = currentResp.total;
        nextOffset.value = Math.max(nextOffset.value, currentOffset + currentResp.items.length);
    }

    if (prevResp?.items && currentOffset > 0) {
        replaceItemsAtOffset(prevOffset, prevResp.items);
        total.value = prevResp.total;
    }

    if (openedVendor.value) {
        const refreshed = vendors.value.find((v) => v.id === openedVendor.value!.id);
        if (refreshed) {
            openedVendor.value = refreshed;
        }
    }
}

const dropdownOptions = [{ label: "View Notes", key: "view-notes" }];

function handleDropdownSelect(key: string | number, row: Vendor) {
    if (key === "view-notes") {
        const entity = vendors.value.find((v) => v.id === row.id) || null;
        if (!entity) {
            message.error("Vendor not found");
            return;
        }
        openedVendor.value = entity;
        vendorId.value = entity.id;
        showDrawer.value = true;
    }
}

function handleCommentCreated(comment: Comment) {
    if (openedVendor.value) {
        const existing = (openedVendor.value as any).comments || [];
        openedVendor.value = {
            ...openedVendor.value,
            comments: [{ ...comment }, ...existing],
        } as Vendor;
    }
    const idx = vendors.value.findIndex((v) => v.id === openedVendor.value?.id);
    if (idx >= 0) {
        const target = vendors.value[idx] as any;
        const existing = target.comments || [];
        vendors.value[idx] = {
            ...target,
            comments: [{ ...comment }, ...existing],
        } as Vendor;
    }
}

function handleCommentUpdated(updated: Comment) {
    if (openedVendor.value) {
        const list = (openedVendor.value as any).comments || [];
        const idx = list.findIndex((c: Comment) => c.id === updated.id);
        if (idx >= 0) {
            const newList = [...list];
            newList[idx] = { ...newList[idx], ...updated } as Comment;
            openedVendor.value = {
                ...openedVendor.value,
                comments: newList,
            } as Vendor;
        }
    }
    const vIdx = vendors.value.findIndex((v) => v.id === openedVendor.value?.id);
    if (vIdx >= 0) {
        const target: any = vendors.value[vIdx];
        const list: Comment[] = target.comments || [];
        const idx = list.findIndex((c: Comment) => c.id === updated.id);
        if (idx >= 0) {
            const newList = [...list];
            newList[idx] = { ...newList[idx], ...updated } as Comment;
            vendors.value[vIdx] = { ...target, comments: newList } as Vendor;
        }
    }
}

function handleCommentDeleted(commentId: string) {
    if (openedVendor.value) {
        const filtered = ((openedVendor.value as any).comments || []).filter(
            (c: Comment) => c.id !== commentId,
        );
        openedVendor.value = {
            ...openedVendor.value,
            comments: filtered,
        } as Vendor;
    }
    const idx = vendors.value.findIndex((v) => v.id === openedVendor.value?.id);
    if (idx >= 0) {
        const target: any = vendors.value[idx];
        const filtered = (target.comments || []).filter((c: Comment) => c.id !== commentId);
        vendors.value[idx] = {
            ...target,
            comments: filtered,
        } as Vendor;
    }
}

function openNotesForRow(row: Vendor) {
    const entity = vendors.value.find((v) => v.id === row.id) || row;
    openedVendor.value = entity;
    vendorId.value = entity.id;
    drawerTab.value = "notes";
    showDrawer.value = true;
}

const columns = computed<DataTableColumns<Vendor>>(() => [
    {
        title: "NAME",
        key: "name",
        width: isMobile.value ? 200 : "25%",
        minWidth: isMobile.value ? 200 : 200,
        render: (row) => h("span", { class: "font-semibold" }, row.name),
    },
    {
        title: "GL CODE",
        key: "gl_account_id",
        width: isMobile.value ? 150 : "15%",
        minWidth: isMobile.value ? 150 : 150,
        render: (row) => row.gl_account_id || "-",
    },
    {
        title: "NOTES",
        key: "comments",
        width: isMobile.value ? 200 : "30%",
        minWidth: isMobile.value ? 200 : 300,
        render: (row) =>
            renderNotesCell({
                row,
                onOpenNotes: openNotesForRow,
            }),
    },
    {
        title: "TAGS",
        key: "tags",
        width: isMobile.value ? 200 : "25%",
        minWidth: isMobile.value ? 200 : 250,
        render: () => "-",
    },
    {
        title: "",
        key: "actions",
        width: 60,
        render: (row) =>
            h(
                NDropdown,
                {
                    options: dropdownOptions,
                    trigger: "click",
                    onSelect: (key: string | number) => handleDropdownSelect(key, row),
                },
                {
                    default: () =>
                        h(
                            "button",
                            {
                                class: "flex items-center justify-center w-full h-full cursor-pointer",
                            },
                            [h("span", { class: "material-symbols-outlined" }, "more_vert")],
                        ),
                },
            ),
    },
]);

const loading = computed(() => loadingVendors.value);
</script>

<template>
    <div class="flex flex-col gap-4">
        <div class="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <proper-text
                v-model="search"
                placeholder="Search vendors..."
                class="w-full sm:w-80"
                :live-preview="false"
                :show-prefix="true"
                :clearable="true"
            />
        </div>

        <div class="overflow-x-auto min-w-0">
            <n-data-table
                :columns="columns"
                :data="vendors"
                :loading="loading || isFetchingMore"
                :bordered="true"
                :single-line="false"
                :single-column="false"
                :scroll-x="isMobile ? 950 : undefined"
                size="small"
                :class="['rounded-lg']"
                :max-height="isMobile ? '60vh' : '80vh'"
                :on-scroll="handleScroll"
            >
                <template #loading>
                    <div
                        class="flex items-center justify-center mt-16 gap-2 bg-white p-3 rounded-lg"
                    >
                        <n-spin size="small" />
                        <p class="text-sm text-gray-500">Loading vendors</p>
                    </div>
                </template>
                <template #empty>
                    <div class="py-6 text-sm text-gray-400">No vendors found</div>
                </template>
            </n-data-table>
        </div>
    </div>
    <notes-drawer
        v-model:show="showDrawer"
        v-model:tab="drawerTab"
        :entity="openedVendor"
        type="vendor"
        :auto-focus-on-open="true"
        @comment-created="handleCommentCreated"
        @comment-updated="handleCommentUpdated"
        @comment-deleted="handleCommentDeleted"
        @refetch="refetchPages"
    />
</template>
